/**
 * MarketingShowcase — the "not boring anymore" section for the homepage.
 * Renders directly below the hero CTAs. Combines:
 *   (1) a live product cockpit preview (SAP + D365 tabbed mock with animated
 *       KPI ticker) — proves what Capittance actually does
 *   (2) a 12-col bento grid of 4 flagship capabilities with tiny visual mocks
 *
 * Design blueprint: /app/design_guidelines.json (Bold Executive/Finance
 * + Editorial Premium + Vibrant SaaS). Palette used here mirrors the dark
 * poster aesthetic — deep navy #0A1428 canvas with #C9A66B gold accents and
 * vibrant SaaS colour pops (violet / blue / emerald) on the dashboard mocks.
 */
import React, { useEffect, useState } from "react";
import {
  Bot, Rocket, GitBranch, HeartPulse, TrendingUp, Sparkles,
  ShieldCheck, ArrowRight, Zap, Radio,
} from "lucide-react";

const KPIS_SAP = [
  { label: "Revenue",  base: 412.4, unit: "M", delta: "+4.1%", up: true },
  { label: "Pipeline", base:  88.2, unit: "M", delta: "+12%",  up: true },
  { label: "EBITDA",   base:  18.2, unit: "%", delta: "+0.4",  up: true },
  { label: "Cash",     base:  54.1, unit: "M", delta: "-2.1%", up: false },
];
const KPIS_D365 = [
  { label: "Bookings", base:  96.7, unit: "M", delta: "+8.2%", up: true },
  { label: "Backlog",  base: 310.4, unit: "M", delta: "+3%",   up: true },
  { label: "Margin",   base:  24.6, unit: "%", delta: "+0.7",  up: true },
  { label: "DSO",      base:  41.2, unit: "d", delta: "-1.2d", up: true },
];

function LiveCockpit() {
  const [tab, setTab] = useState("sap");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1200);
    return () => clearInterval(t);
  }, []);

  const kpis = tab === "sap" ? KPIS_SAP : KPIS_D365;
  const jitter = (n) => (n + Math.sin(tick * 0.9) * 0.4).toFixed(1);
  const tabColor = tab === "sap" ? "#3B82F6" : "#8B5CF6";

  return (
    <div
      className="relative rounded-2xl overflow-hidden group"
      style={{ background: "#0A1428", boxShadow: "0 30px 80px -20px rgba(4,9,20,0.55), 0 12px 24px -10px rgba(4,9,20,0.4)" }}
      data-testid="marketing-live-cockpit"
    >
      {/* subtle grain */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
           style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
      {/* gold hairline frame */}
      <div className="absolute inset-3 border border-[#C9A66B]/25 rounded-xl pointer-events-none" />

      {/* Header bar */}
      <div className="relative px-6 pt-6 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-400" />
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <span className="ml-3 font-mono text-[10px] tracking-[0.24em] text-[#C9A66B] uppercase">Executive Delivery Cockpit</span>
        </div>
        <div className="flex items-center gap-2 font-mono text-[10px] text-zinc-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
        </div>
      </div>

      {/* Tab strip */}
      <div className="relative px-6 flex items-center gap-2 mb-3">
        {[
          { id: "sap",  label: "SAP S/4HANA",         c: "#3B82F6" },
          { id: "d365", label: "Dynamics 365 F&O",    c: "#8B5CF6" },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`cockpit-tab-${t.id}`}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${tab === t.id ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"}`}
            style={{ borderBottom: tab === t.id ? `2px solid ${t.c}` : "2px solid transparent" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* KPI grid */}
      <div className="relative px-6 grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {kpis.map((k, i) => (
          <div key={k.label} className="rounded-lg p-3 relative overflow-hidden"
               style={{ background: "#101E36", border: "1px solid #1E3055" }}>
            <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-500">{k.label}</div>
            <div className="text-white font-display text-2xl font-extrabold tracking-tight mt-1">
              {k.unit === "d" ? Math.round(k.base + Math.sin(tick + i)) : jitter(k.base)}
              <span className="text-zinc-400 text-sm ml-0.5">{k.unit}</span>
            </div>
            <div className={`text-[11px] font-mono font-semibold mt-1 ${k.up ? "text-emerald-400" : "text-rose-400"}`}>
              {k.up ? "▲" : "▼"} {k.delta}
            </div>
          </div>
        ))}
      </div>

      {/* Sparkline strip */}
      <div className="relative px-6 pb-3">
        <div className="rounded-lg p-3" style={{ background: "#101E36", border: "1px solid #1E3055" }}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-400">Readiness · 12-week horizon</span>
            <span className="font-mono text-[10px] text-emerald-400">72% · on plan</span>
          </div>
          <svg viewBox="0 0 400 60" className="w-full h-14" preserveAspectRatio="none">
            {/* gridlines */}
            {[15, 30, 45].map(y => (
              <line key={y} x1="0" x2="400" y1={y} y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
            ))}
            {/* SAP or D365 line */}
            <polyline
              fill="none" stroke={tabColor} strokeWidth="2" strokeLinecap="round"
              points={Array.from({ length: 24 }).map((_, i) => {
                const x = (i / 23) * 400;
                const y = 30 + Math.sin((i + tick * 0.3) * 0.5) * 10 - i * 0.3;
                return `${x},${y}`;
              }).join(" ")}
            />
            {/* fill */}
            <polyline
              fill={tabColor} opacity="0.12" stroke="none"
              points={
                "0,60 " +
                Array.from({ length: 24 }).map((_, i) => {
                  const x = (i / 23) * 400;
                  const y = 30 + Math.sin((i + tick * 0.3) * 0.5) * 10 - i * 0.3;
                  return `${x},${y}`;
                }).join(" ") +
                " 400,60"
              }
            />
          </svg>
        </div>
      </div>

      {/* RAG chip row */}
      <div className="relative px-6 pb-6 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block mr-1.5 animate-pulse" />
          16 On Track
        </span>
        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/25">
          5 At Risk
        </span>
        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/25">
          3 Off Track
        </span>
        <span className="ml-auto text-[10px] font-mono text-zinc-500">$4.8M budget · 134 SAP Activate steps</span>
      </div>
    </div>
  );
}

function BentoCard({ icon: Icon, tag, title, body, accent, testid, children }) {
  return (
    <div
      className="relative rounded-2xl p-6 border overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
      style={{ background: "#0A1428", borderColor: "#1E3055" }}
      data-testid={testid}
    >
      <div
        className="absolute -top-16 -right-16 w-52 h-52 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity"
        style={{ background: accent }}
      />
      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: accent, color: "#0A1428" }}>
            <Icon size={18} strokeWidth={2.4} />
          </div>
          <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#C9A66B]">{tag}</span>
        </div>
        <h3 className="font-display text-xl font-extrabold text-white leading-tight mb-2">{title}</h3>
        <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
        {children && <div className="mt-4">{children}</div>}
      </div>
    </div>
  );
}

export default function MarketingShowcase() {
  return (
    <section className="mt-20 max-w-6xl mx-auto anim-in" data-testid="marketing-showcase" style={{ animationDelay: "760ms" }}>
      {/* Eyebrow row */}
      <div className="flex items-center justify-center gap-3 mb-6">
        <span className="h-px w-12 bg-[#C9A66B]/40" />
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C9A66B]">The delivery cockpit — live</span>
        <span className="h-px w-12 bg-[#C9A66B]/40" />
      </div>

      {/* Live cockpit + headline copy */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-12">
        <div className="lg:col-span-3">
          <LiveCockpit />
        </div>
        <div className="lg:col-span-2 lg:pt-4">
          <h2 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight text-zinc-900 leading-tight">
            Not another dashboard.<br />
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(120deg, #0A1428 0%, #7A6640 100%)" }}>
              Your executive command centre.
            </span>
          </h2>
          <p className="text-zinc-700 mt-4 leading-relaxed">
            Every project, live. Cash burn, milestones, risks, RAG signals, and Claude 4.5 insights — one browser tab, one truth. No more Monday-morning scavenger hunt.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            {[
              { icon: Radio,        text: "Real-time SAP S/4HANA & Dynamics 365 cockpits" },
              { icon: Bot,          text: "NAVBOT — Claude 4.5 executive AI with proactive briefings" },
              { icon: Rocket,       text: "20-slide Board Pack generated in 60 seconds" },
              { icon: HeartPulse,   text: "AI-powered Diagnostics that name what's off-track" },
            ].map((r, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg grid place-items-center shrink-0"
                      style={{ background: "#0A1428", color: "#C9A66B" }}>
                  <r.icon size={14} strokeWidth={2.4}/>
                </span>
                <span className="text-sm font-semibold text-zinc-800">{r.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bento grid — 4 capabilities */}
      <div className="mt-14">
        <div className="flex items-center justify-center gap-3 mb-6">
          <span className="h-px w-12 bg-[#C9A66B]/40" />
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C9A66B]">Four capabilities · one platform</span>
          <span className="h-px w-12 bg-[#C9A66B]/40" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <BentoCard
            testid="bento-copilot"
            icon={Bot} tag="NAVBOT · C-Suite only" accent="#8B5CF6"
            title="Ask any board-level question."
            body="NAVBOT (Claude Sonnet 4.5) trained on your live portfolio — every RAID, milestone and KPI — turns a five-word question into a boardroom-ready answer. Activates the moment your C-Suite subscription goes live."
          >
            <div className="rounded-lg p-3 text-[11px] leading-relaxed"
                 style={{ background: "#101E36", border: "1px solid #1E3055", color: "#CBD5E1" }}>
              <div className="text-[9px] font-mono tracking-[0.16em] uppercase text-[#C9A66B] mb-1">CFO ASK</div>
              <div className="text-zinc-300">&ldquo;Summarise this quarter&rsquo;s RAID for CFO&rdquo;</div>
              <div className="border-l-2 border-[#C9A66B] pl-2 mt-2 text-white/90">3 elevated risks · budget 6% under · UAT starts Jun 15. Recommend approve stage-gate 3.</div>
            </div>
          </BentoCard>

          <BentoCard
            testid="bento-boardpack"
            icon={Rocket} tag="Board Pack" accent="#F59E0B"
            title="20 slides. 60 seconds. Zero rebuilds."
            body="Live KPIs, RAID, budget burn and decisions rendered into a boardroom-ready PPT. Upload your theme? $50 custom-swap keeps your brand intact."
          >
            <div className="grid grid-cols-4 gap-1">
              {["Cover","Portfolio","Health","Budget","Risks","Milestones","Value","Next 90d"].map((s, i) => (
                <div key={s} className="rounded aspect-square flex items-end p-1 text-[7px] font-mono"
                     style={{ background: i === 0 ? "#C9A66B" : "#101E36", color: i === 0 ? "#0A1428" : "#94A3B8", border: "1px solid #1E3055" }}>
                  <span className="leading-none">{s}</span>
                </div>
              ))}
            </div>
          </BentoCard>

          <BentoCard
            testid="bento-diagnostics"
            icon={HeartPulse} tag="Diagnostics" accent="#EF4444"
            title="Point exactly what's off-track."
            body="Schedule slippage · Budget overrun · Critical-path delay · Go-live at risk · Benefits at risk. Every red signal names the milestone, task or risk responsible."
          >
            <div className="space-y-1.5">
              {[
                { l: "Security Audit sign-off · 86d late",  s: "CRIT",  c: "#EF4444" },
                { l: "UAT Phase Begin · 68d late",           s: "HIGH",  c: "#F59E0B" },
                { l: "Cloud vendor lock-in · Alex Torres",   s: "HIGH",  c: "#F59E0B" },
              ].map((r) => (
                <div key={r.l} className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[11px]"
                     style={{ background: "#101E36", border: "1px solid #1E3055", color: "#CBD5E1" }}>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: r.c }} />
                  <span className="flex-1 truncate">{r.l}</span>
                  <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: r.c }}>{r.s}</span>
                </div>
              ))}
            </div>
          </BentoCard>

          <BentoCard
            testid="bento-cockpits"
            icon={GitBranch} tag="ERP Cockpits" accent="#3B82F6"
            title="S/4HANA & D365, side-by-side."
            body="Read-only OData / OAuth connectors for S/4HANA, Dynamics 365 and Oracle Cloud ERP. Revenue, pipeline, EBITDA and readiness surfaced live once your tenant credentials are validated — no data warehouse required. Secrets stay server-side, always."
          >
            <div className="grid grid-cols-2 gap-2">
              {[{ n: "SAP", c: "#3B82F6", v: "72%" }, { n: "D365", c: "#8B5CF6", v: "68%" }].map(x => (
                <div key={x.n} className="rounded-lg p-2.5" style={{ background: "#101E36", border: "1px solid #1E3055" }}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">{x.n}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  </div>
                  <div className="h-1.5 w-full rounded-full mb-1" style={{ background: "#1E3055" }}>
                    <div className="h-1.5 rounded-full" style={{ background: x.c, width: x.v }} />
                  </div>
                  <div className="font-mono text-[10px] font-semibold" style={{ color: x.c }}>{x.v} ready</div>
                </div>
              ))}
            </div>
          </BentoCard>
        </div>
      </div>

      {/* Divider quote */}
      <div className="mt-16 flex flex-col items-center text-center">
        <Sparkles size={20} className="text-[#C9A66B] mb-3" />
        <blockquote className="max-w-2xl font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 leading-snug">
          &ldquo;Every project. Live. One truth. Zero Monday scavenger hunts.&rdquo;
        </blockquote>
        <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          — What every CIO wishes they said on the last board call
        </div>
      </div>

      {/* Trust ribbon */}
      <div className="mt-14 rounded-2xl p-6 md:p-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
           style={{ background: "#0A1428", border: "1px solid #1E3055" }}>
        {[
          { icon: ShieldCheck, label: "Stripe-secured payments" },
          { icon: Zap,         label: "GDPR-ready · EU sub-processors" },
          { icon: TrendingUp,  label: "SAP Activate · 134-step compliant" },
          { icon: ArrowRight,  label: "One-click cancel · no lock-in" },
        ].map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-white font-semibold text-[13px]">
            <span className="w-7 h-7 rounded-lg grid place-items-center shrink-0 bg-[#C9A66B]/15 text-[#C9A66B]">
              <r.icon size={13} strokeWidth={2.4}/>
            </span>
            {r.label}
          </div>
        ))}
      </div>
    </section>
  );
}
